import React, { useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';

import { RootState } from 'store/reducer';
import { configSelector, selectAccessToken } from 'store/config/selectors';
import { FormDefinition } from 'store/booking-add-ons/types';
import { useLocale } from 'hooks/use-locale.hook';

import { createFormRendererRemote } from './form-renderer-remote';
import { FormRendererValidationState, VendorFormRendererProps } from './types';

interface Props {
  definition: FormDefinition;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  onValidate: (state: FormRendererValidationState) => void;
}

export const VendorFormRenderer = ({ definition, values, onChange, onValidate }: Props): JSX.Element => {
  const store = useStore<RootState>();
  const locale = useLocale();
  const { apiUrl, buildingUuid } = configSelector(store.getState());

  /* Read through the store, not a closure: a token captured at mount would 401 silently after a refresh. */
  const getAuthToken = useCallback(() => Promise.resolve(selectAccessToken(store.getState()) ?? ''), [store]);

  /* Created once: building it in the render remounts the form each pass, losing anything half-typed. */
  const RemoteFormRenderer = useMemo(() => createFormRendererRemote(), []);

  const props: VendorFormRendererProps = {
    /* Must be absolute: the remote runs `new URL(apiUrl).origin`, and a relative URL throws inside it. */
    apiUrl,
    getAuthToken,
    locale,
    buildingUuid: buildingUuid ?? undefined,
    definition,
    /* The stateless renderer defaults `readOnly = true`, so omitting this renders a form nobody can fill. */
    readOnly: false,
    values,
    onChange,
    onValidate,
    viewerRole: 'tenant',
  };

  /*
   * Spread, never `props={props}`: the bridge forwards by rest on both sides and reads no key named
   * `props`, so nesting leaves the remote with `apiUrl` undefined. `RemoteComponentProps` declares a
   * `props` field anyway, so the compiler will not catch it. The annotation above is the contract,
   * not this JSX. Keep keys unconditional (the bridge's effect deps are `Object.values(props)`), and
   * off the names it consumes: moduleName, memoryRoute, basename, providerInfo, className, style,
   * fallback, loading, exportName.
   */
  return <RemoteFormRenderer {...props} />;
};
